import { Injectable } from '@angular/core';
import { BaseProvider } from './base';
import { JobProvider, JobType, JobResponse, JobState } from './job';
import { Dataset, DatasetProvider } from './dataset';
import { WhiteLabelProvider } from './white-label';
import { BoostProvider, STATE_DESCRIPTION_RECALCULATE } from './boost';
import { TranslateService } from '@ngx-translate/core';

/**
 * Return string descriptions for various elements of the sidebar job component
 */
@Injectable()
export class SidebarJobProvider extends BaseProvider {
    constructor(
        private jobProvider: JobProvider,
        private datasetProvider: DatasetProvider,
        private whiteLabelProvider: WhiteLabelProvider,
        private boostProvider: BoostProvider,
        private translate: TranslateService
    ) {
        super();
    }

    /**
     * Return a brief descriptor of what the job is currently doing
     */
    public getAction(job: JobResponse): string {
        switch (job.type) {
            case JobType.Retrain:
                return this.getRetrainAction(job);

            case JobType.Optimize:
                return this.getOptimizeAction(job);

            case JobType.Freeze:
                return this.getFreezeAction(job);

            case JobType.Train:
                return this.getTrainAction(job);

            case JobType.AnomalyHiFi:
                return this.getAnomalyHiFiAction(job);

            case JobType.BrainExport:
                return this.getBrainExportAction(job);

            default:
                return job.state;
        }
    }

    /**
     * Return the text for the tooltip component of the specified job in the sidebar
     */
    public getTooltip(job: JobResponse, shouldSeeWhiteLabelCopy: boolean, isIndeterminate: boolean): string {
        const br = '<br />';
        const rawDatasetText = this.getTooltipDataset(job);
        const datasetText = rawDatasetText ? `${rawDatasetText}${br}` : '';

        const action = this.getTooltipAction(job, shouldSeeWhiteLabelCopy);
        const status = this.getTooltipStatus(job, isIndeterminate);
        const advice = this.getTooltipAdvice(job);

        return `${datasetText}${action}${br}${status}${br}${advice}`;
    }

    private getRetrainAction(job: JobResponse): string {
        switch (job.state) {
            case JobState.WAITING:
                return this.translate.instant('Waiting');

            case JobState.READY:
            case JobState.PROCESSING:
                return this.translate.instant('Retraining');

            case JobState.CANCEL_REQUESTED:
            case JobState.CANCELING:
                return this.translate.instant('Canceling');

            case JobState.CANCELED:
                return this.translate.instant('Canceled');

            case JobState.ERROR:
                return this.translate.instant('Error');

            case JobState.DONE:
                return this.translate.instant('Retrained');

            default:
                return job.state;
        }
    }

    private getOptimizeAction(job: JobResponse): string {
        switch (job.state) {
            case JobState.WAITING:
                return this.translate.instant('Waiting');

            case JobState.READY:
            case JobState.PROCESSING:
                return this.translate.instant('Optimizing');

            case JobState.CANCEL_REQUESTED:
            case JobState.CANCELING:
                return this.translate.instant('Canceling');

            case JobState.CANCELED:
                return this.translate.instant('Canceled');

            case JobState.ERROR:
                return this.translate.instant('Error');

            case JobState.DONE:
                return this.translate.instant('Optimized');

            default:
                return job.state;
        }
    }

    private getFreezeAction(job: JobResponse): string {
        const dataset = this.getJobDataset(job);
        const isRerunning = dataset && dataset.hasFrozenBrain();

        switch (job.state) {
            case JobState.WAITING:
                return this.translate.instant('Waiting');

            case JobState.READY:
            case JobState.PROCESSING:
                // BB-5132 For now, display 'Rebuilding' only when a previously built frozen brain is present AND
                // when a subsequent build is still running
                return isRerunning ? this.translate.instant('Rebuilding') : this.translate.instant('Building');

            case JobState.CANCEL_REQUESTED:
            case JobState.CANCELING:
                return this.translate.instant('Canceling');

            case JobState.CANCELED:
                return this.translate.instant('Canceled');

            case JobState.ERROR:
                return this.translate.instant('Error');

            case JobState.DONE:
                return this.translate.instant('Built');

            default:
                return job.state;
        }
    }

    private getTrainAction(job: JobResponse): string {
        switch (job.state) {
            case JobState.WAITING:
                return this.translate.instant('Waiting');

            case JobState.READY:
            case JobState.PROCESSING:
                return this.translate.instant('Training');

            case JobState.CANCEL_REQUESTED:
            case JobState.CANCELING:
                return this.translate.instant('Canceling');

            case JobState.CANCELED:
                return this.translate.instant('Canceled');

            case JobState.ERROR:
                return this.translate.instant('Error');

            case JobState.DONE:
                return this.translate.instant('Trained');

            default:
                return job.state;
        }
    }

    private getAnomalyHiFiAction(job: JobResponse): string {
        const dataset = this.getJobDataset(job);
        const isRerunning = dataset && dataset.hasAnomalyHiFiBrain();

        switch (job.state) {
            case JobState.WAITING:
                return this.translate.instant('Waiting');

            case JobState.READY:
            case JobState.PROCESSING:
                return isRerunning ? this.translate.instant('Rebuilding') : this.translate.instant('Building');

            case JobState.CANCEL_REQUESTED:
            case JobState.CANCELING:
                return this.translate.instant('Canceling');

            case JobState.CANCELED:
                return this.translate.instant('Canceled');

            case JobState.ERROR:
                return this.translate.instant('Error');

            case JobState.DONE:
                return this.translate.instant('Built');

            default:
                return job.state;
        }
    }

    private getBrainExportAction(job: JobResponse): string {
        switch (job.state) {
            case JobState.WAITING:
                return this.translate.instant('Waiting');

            case JobState.READY:
            case JobState.PROCESSING:
                return this.translate.instant('Exporting');

            case JobState.CANCEL_REQUESTED:
            case JobState.CANCELING:
                return this.translate.instant('Canceling');

            case JobState.CANCELED:
                return this.translate.instant('Canceled');

            case JobState.ERROR:
                return this.translate.instant('Error');

            case JobState.DONE:
                return this.translate.instant('Exported');

            default:
                return job.state;
        }
    }

    /**
     * Return the dataset associated with the specified job.
     * Return null if no dataset is associated with the job
     */
    private getJobDataset(job: JobResponse): Dataset | null {
        if (job.datasetIdentifier) {
            return this.datasetProvider.getDatasetById(job.datasetIdentifier) || null;
        }

        return null;
    }

    private getTooltipDataset(job: JobResponse): string | null {
        if (job.datasetIdentifier) {
            const dataset = this.datasetProvider.getDatasetById(job.datasetIdentifier);

            if (dataset) {
                return `${dataset.name} ${this.translate.instant('Dataset').toLowerCase()}`;
            }
        }

        return null;
    }

    private getTooltipAction(job: JobResponse, shouldSeeWhiteLabelCopy: boolean): string {
        return `${this.getSidebarJobTooltipJobType(job)} ${this.getSidebarJobTooltipBrainType(
            job,
            shouldSeeWhiteLabelCopy
        )}`;
    }

    private getSidebarJobTooltipJobType(job: JobResponse): string {
        let dataset;
        let isRebuilding;

        switch (job.type) {
            case JobType.Retrain:
                return this.translate.instant('Retraining');

            case JobType.Optimize:
                return this.translate.instant('Optimizing');

            case JobType.Freeze:
                dataset = this.getJobDataset(job);

                // BB-5132 For now, display 'Rebuilding' only when a previously built frozen brain is present AND
                // when a subsequent build is still running
                isRebuilding = dataset && dataset.hasFrozenBrain() && !this.jobProvider.isJobDone(job.state);

                return isRebuilding ? this.translate.instant('Rebuilding') : 'Building';

            case JobType.Train:
                return this.translate.instant('Training');

            case JobType.AnomalyHiFi:
                dataset = this.getJobDataset(job);

                // BB-5132 For now, display 'Rebuilding' only when a previously built hifi brain is present AND
                // when a subsequent build is still running
                isRebuilding = dataset && dataset.hasAnomalyHiFiBrain() && !this.jobProvider.isJobDone(job.state);

                return isRebuilding ? this.translate.instant('Rebuilding') : 'Building';

            case JobType.BrainExport:
                return this.translate.instant('Brain Export');

            default:
                return job.state;
        }
    }

    private getSidebarJobTooltipBrainType(job: JobResponse, shouldSeeWhiteLabelCopy: boolean): string {
        switch (job.type) {
            case JobType.Retrain:
            case JobType.Optimize:
            case JobType.AnomalyHiFi:
                return this.datasetProvider.getRenderableDatasetTypeFromDatasetId(job.datasetIdentifier);

            case JobType.Freeze:
                return shouldSeeWhiteLabelCopy
                    ? `${this.whiteLabelProvider.getBrainName()} ${this.translate.instant('Brain')}`
                    : this.translate.instant('Static Classifier');

            case JobType.Train:
                return this.translate.instant('Detector');

            default:
                return '';
        }
    }

    private getTooltipStatus(job: JobResponse, isIndeterminate: boolean): string {
        switch (job.state) {
            case JobState.READY:
                return this.translate.instant('Ready');

            case JobState.WAITING:
                return this.translate.instant('Waiting');

            case JobState.PROCESSING:
                // We will follow the same pattern as on train/retrain/optimize screen:
                // in the components we subscribe to boost events, and in the case when job has stateDescription
                // we publish BoostRecalculating event (in the boost provider), in which case we show Finishing up... text
                if (job.stateDescription == STATE_DESCRIPTION_RECALCULATE) {
                    return `${this.translate.instant('Finishing up')}...`;
                } else if (!isIndeterminate) {
                    const translateReplacements = {
                        progress: Math.round(this.jobProvider.getProgressPercentage(job))
                    };
                    return this.translate.instant('[[progress]]% complete', translateReplacements);
                } else {
                    return `${this.translate.instant('Processing')}...`;
                }

            case JobState.CANCELING:
            case JobState.CANCEL_REQUESTED:
                return this.translate.instant('Canceling');

            case JobState.CANCELED:
                return this.translate.instant('Canceled');

            case JobState.ERROR:
                return this.translate.instant('Error');

            case JobState.DONE:
                const translateReplacements = {
                    progress: 100
                };
                return this.translate.instant('[[progress]]% complete', translateReplacements);

            default:
                return '';
        }
    }

    private getTooltipAdvice(job: JobResponse): string {
        if (job.state == JobState.DONE) {
            return this.translate.instant('Click to evaluate your results');
        } else {
            return this.translate.instant('Click for more details');
        }
    }
}



// WEBPACK FOOTER //
// ./src/providers/sidebar-job.ts